import {
  ArrowRight,
  Bell,
  Database,
  FlaskConical,
  Workflow,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useDataCatalogue } from "../data-explorer-api";
import { MeshGradientBackground } from "../mesh-gradient-background";
import { useNotificationTypesConfig } from "../notifications-admin-api";
import { useProfile } from "../profile-api";
import {
  formatRelativeToNow,
  useQaRunsList,
  useQaSchedule,
} from "../qa-automation-api";
import { useDocumentTitle } from "../use-document-title";
import styles from "./home.module.css";

type CapabilityCardProps = {
  to: string;
  icon: ReactNode;
  title: string;
  description: string;
  signal: ReactNode;
  statusTone?: HealthTone;
};

type HealthTone = "success" | "warning" | "error";

// Same pink-to-orange palette as Norch's animated app-shell hero.
const HOME_MESH_COLORS = ["#EE712A", "#E9366B", "#F2985E", "#F0524A"];

function CapabilityCard({ to, icon, title, description, signal, statusTone }: CapabilityCardProps) {
  return (
    <Link className={styles.capabilityCard} data-status-tone={statusTone} to={to}>
      <span className={styles.cardIcon}>{icon}</span>
      <span className={styles.cardContent}>
        <span className={styles.cardTitleRow}>
          <span className={styles.cardTitle}>{title}</span>
          <ArrowRight size={15} aria-hidden="true" />
        </span>
        <span className={styles.cardDescription}>{description}</span>
        <span className={styles.cardSignal}>{signal}</span>
      </span>
    </Link>
  );
}

function QuerySignal({ loading, error, children }: { loading: boolean; error: boolean; children: ReactNode }) {
  if (loading) return <span className={`${styles.signalPill} ${styles.signalLoading}`}>Loading</span>;
  if (error) return <span className={`${styles.signalPill} ${styles.signalError}`}>Unavailable</span>;
  return <>{children}</>;
}

function qaStatusTone(status: string | undefined, scheduleEnabled: boolean | undefined): HealthTone {
  const normalised = status?.toLowerCase();
  if (normalised === "passed" || normalised === "complete" || normalised === "completed" || normalised === "success") {
    return "success";
  }
  if (normalised === "failed" || normalised === "error") return "error";
  if (normalised === "running" || normalised === "in_progress") return "warning";
  return status ? "warning" : scheduleEnabled ? "success" : "error";
}

function signalToneClass(tone: HealthTone): string {
  if (tone === "success") return styles.signalSuccess;
  if (tone === "warning") return styles.signalWarning;
  return styles.signalError;
}

export function HomePage() {
  useDocumentTitle("Home");

  const profile = useProfile();
  const qaRuns = useQaRunsList();
  const qaSchedule = useQaSchedule();
  const notificationTypes = useNotificationTypesConfig();
  const postgres = useDataCatalogue("postgres");
  const dynamodb = useDataCatalogue("dynamodb");
  const s3 = useDataCatalogue("s3");

  const firstName = profile.data?.profile.first_name?.trim();
  const latestRun = qaRuns.data?.runs[0];
  const configuredProviders = notificationTypes.data
    ? ["email", "whatsapp", "slack", "push"].filter((channel) =>
        notificationTypes.data.some(
          (type) => type.channels[channel as keyof typeof type.channels]?.status === "active",
        ),
      ).length
    : 0;
  const dataResourceCount =
    (postgres.data?.length ?? 0) +
    (dynamodb.data?.length ?? 0) +
    (s3.data?.length ?? 0);
  const dataLoading = postgres.isLoading || dynamodb.isLoading || s3.isLoading;
  const dataError = postgres.isError || dynamodb.isError || s3.isError;
  const qaTone = qaStatusTone(latestRun?.status, qaSchedule.data?.enabled);
  const qaStatusToneForCard = qaRuns.isLoading || qaSchedule.isLoading
    ? undefined
    : qaRuns.isError || qaSchedule.isError
      ? "error"
      : qaTone;

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <MeshGradientBackground
          bare
          hostClassName={styles.heroMesh}
          colors={HOME_MESH_COLORS}
        />
        <div>
          <p className={styles.eyebrow}>PCG Platform</p>
          <h1 className={styles.heroTitle}>Welcome back{firstName ? `, ${firstName}` : ""}.</h1>
          <p className={styles.heroDescription}>
            Monitor automation, inspect data and manage how the platform communicates.
          </p>
        </div>
        <div className={styles.environmentBadge}>
          <span className={styles.environmentDot} aria-hidden="true" />
          Dev and production
        </div>
      </section>

      <section aria-label="Platform areas">
        <div className={styles.capabilityGrid}>
          <CapabilityCard
            to="/workflows"
            icon={<Workflow />}
            title="Workflows"
            description="Definitions, active runs, schedules and infrastructure jobs."
            statusTone="success"
            signal={
              <span className={styles.signalRow}>
                <span className={`${styles.signalPill} ${styles.signalSuccess}`}>Ready</span>
                <span className={styles.signalDetail}>Catalogue and run previews</span>
              </span>
            }
          />
          <CapabilityCard
            to="/qa-automation"
            icon={<FlaskConical />}
            title="QA Automation"
            description="Run history, verdict trends, test detail and recordings."
            statusTone={qaStatusToneForCard}
            signal={
              <QuerySignal loading={qaRuns.isLoading || qaSchedule.isLoading} error={qaRuns.isError || qaSchedule.isError}>
                <span className={styles.signalRow}>
                  <span className={`${styles.signalPill} ${signalToneClass(qaTone)}`}>
                    {latestRun?.status ?? (qaSchedule.data?.enabled ? "Scheduled" : "No runs")}
                  </span>
                  <span className={styles.signalDetail}>
                    {latestRun ? formatRelativeToNow(latestRun.startedAt) : qaSchedule.data?.enabled ? "Automated QA enabled" : "No recent activity"}
                  </span>
                </span>
              </QuerySignal>
            }
          />
          <CapabilityCard
            to="/notifications"
            icon={<Bell />}
            title="Notifications"
            description="Delivery readiness, notification catalogue and user preferences."
            signal={
              <QuerySignal loading={notificationTypes.isLoading} error={notificationTypes.isError}>
                <span className={styles.metricRow}>
                  <span className={styles.metric}><strong>{notificationTypes.data?.length ?? 0}</strong><span>types</span></span>
                  <span className={styles.metricDivider} aria-hidden="true" />
                  <span className={styles.metric}><strong>{configuredProviders}<small>/4</small></strong><span>providers active</span></span>
                </span>
              </QuerySignal>
            }
          />
          <CapabilityCard
            to="/data-explorer"
            icon={<Database />}
            title="Data Explorer"
            description="Browse PostgreSQL, DynamoDB and Amazon S3 from one place."
            signal={
              <QuerySignal loading={dataLoading} error={dataError}>
                <span className={styles.metricRow}>
                  <span className={styles.metric}><strong>{dataResourceCount.toLocaleString("en-GB")}</strong><span>discoverable resources</span></span>
                </span>
              </QuerySignal>
            }
          />
        </div>
      </section>

    </div>
  );
}
